import React from 'react';
import { Box, Typography, Chip, Stack, Avatar, LinearProgress } from '@mui/material';
import { EmojiEvents, TrendingUp, Speed, Star, CompareArrows, AttachMoney } from '@mui/icons-material';
import PremiumCard from '../../ui/PremiumCard';

/**
 * WIDGET ÚNICO: Análisis Competitivo y Benchmarking
 * Comparación con promedios de la industria que Tiendanube NO tiene
 */
export default function CompetitiveAnalysisCard({ statsData = {} }) {
  // Benchmarks de la industria (datos reales de e-commerce)
  const industryBenchmarks = {
    avgTicket: 250000, // $250k promedio industria
    conversionRate: 2.5, // 2.5% promedio
    customerRetention: 25, // 25% retención
    ordersPerCustomer: 1.8, // 1.8 órdenes por cliente
    profitMargin: 20 // 20% margen promedio
  };

  // Calcular comparaciones
  const competitiveAnalysis = React.useMemo(() => {
    const avgTicket = statsData.avgTicket || 0;
    const conversionRate = statsData.conversionRate || 0;
    const totalCustomers = statsData.totalCustomers || 0;
    const totalOrders = statsData.totalOrders || 0;
    const newCustomers = statsData.newCustomers || 0;

    if (avgTicket === 0) return null;

    const ordersPerCustomer = totalCustomers > 0 ? totalOrders / totalCustomers : 0;
    const retentionRate = totalCustomers > 0 ? ((totalCustomers - newCustomers) / totalCustomers) * 100 : 0;
    
    // Comparaciones con la industria
    const metrics = [
      {
        name: 'Ticket Promedio',
        value: avgTicket,
        benchmark: industryBenchmarks.avgTicket,
        performance: (avgTicket / industryBenchmarks.avgTicket) * 100,
        unit: '$',
        icon: <AttachMoney />
      },
      {
        name: 'Tasa de Conversión',
        value: conversionRate,
        benchmark: industryBenchmarks.conversionRate,
        performance: (conversionRate / industryBenchmarks.conversionRate) * 100,
        unit: '%',
        icon: <TrendingUp />
      },
      {
        name: 'Órdenes por Cliente',
        value: ordersPerCustomer,
        benchmark: industryBenchmarks.ordersPerCustomer,
        performance: (ordersPerCustomer / industryBenchmarks.ordersPerCustomer) * 100,
        unit: '',
        icon: <Speed />
      },
      {
        name: 'Retención de Clientes',
        value: retentionRate,
        benchmark: industryBenchmarks.customerRetention,
        performance: (retentionRate / industryBenchmarks.customerRetention) * 100,
        unit: '%',
        icon: <Star />
      }
    ];

    // Calcular score general
    const overallScore = metrics.reduce((sum, metric) => sum + Math.min(metric.performance, 150), 0) / metrics.length;
    
    // Determinar posición competitiva
    let competitivePosition = "Promedio";
    let positionColor = "warning";
    if (overallScore > 120) {
      competitivePosition = "Líder";
      positionColor = "success";
    } else if (overallScore > 80) {
      competitivePosition = "Competitivo";
      positionColor = "info";
    } else {
      competitivePosition = "Oportunidad";
      positionColor = "error";
    }

    return {
      metrics,
      overallScore,
      competitivePosition,
      positionColor,
      strengths: metrics.filter(m => m.performance > 110).map(m => m.name),
      weaknesses: metrics.filter(m => m.performance < 80).map(m => m.name)
    };
  }, [statsData]);

  if (!competitiveAnalysis) {
    return (
      <PremiumCard 
        title="Análisis Competitivo" 
        subtitle="Benchmarking vs industria"
        icon={<CompareArrows />}
        color="warning"
        exclusive
      >
        <Typography variant="body2" color="text.secondary" textAlign="center">
          No hay datos suficientes para análisis competitivo
        </Typography>
      </PremiumCard>
    );
  }

  return (
    <PremiumCard 
      title="Análisis Competitivo"
      subtitle="Benchmarking vs promedio de la industria"
      icon={<CompareArrows />}
      color="warning"
      exclusive
    >
        {/* Score general */}
        <Box mb={3} textAlign="center">
          <Avatar 
            sx={{ 
              width: 64, 
              height: 64, 
              bgcolor: `${competitiveAnalysis.positionColor}.main`,
              mx: 'auto',
              mb: 1
            }}
          >
            <EmojiEvents fontSize="large" />
          </Avatar>
          <Typography variant="h6" color={`${competitiveAnalysis.positionColor}.main`}>
            {competitiveAnalysis.competitivePosition}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Score: {competitiveAnalysis.overallScore.toFixed(0)}/150
          </Typography>
        </Box>

        {/* Métricas comparativas */}
        <Typography variant="subtitle2" gutterBottom>
          📊 Comparación con la Industria
        </Typography>
        <Stack spacing={2} mb={3}>
          {competitiveAnalysis.metrics.map((metric, index) => (
            <Box key={index}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  {React.cloneElement(metric.icon, { fontSize: 'small', color: 'action' })}
                  <Typography variant="body2" fontWeight={500}>
                    {metric.name}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {metric.unit === '$' ? '$' : ''}{metric.value.toLocaleString()}{metric.unit === '%' ? '%' : metric.unit}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <LinearProgress 
                  variant="determinate" 
                  value={Math.min(metric.performance, 150)} 
                  color={metric.performance > 110 ? "success" : metric.performance > 80 ? "info" : "error"}
                  sx={{ flex: 1, height: 6, borderRadius: 3 }}
                />
                <Typography variant="caption" color="text.secondary" minWidth={60}>
                  {metric.performance.toFixed(0)}% vs industria
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>

        {/* Fortalezas y debilidades */}
        <Stack direction="row" spacing={2} mb={3}>
          {competitiveAnalysis.strengths.length > 0 && (
            <Box flex={1}>
              <Typography variant="subtitle2" color="success.main" gutterBottom>
                💪 Fortalezas
              </Typography>
              <Stack spacing={0.5}>
                {competitiveAnalysis.strengths.map((strength, i) => (
                  <Chip 
                    key={i} 
                    label={strength} 
                    size="small" 
                    color="success"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>
          )}
          
          {competitiveAnalysis.weaknesses.length > 0 && (
            <Box flex={1}>
              <Typography variant="subtitle2" color="error.main" gutterBottom>
                🎯 Oportunidades
              </Typography>
              <Stack spacing={0.5}>
                {competitiveAnalysis.weaknesses.map((weakness, i) => (
                  <Chip 
                    key={i} 
                    label={weakness} 
                    size="small" 
                    color="error"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>

        {/* Recomendación estratégica */}
        <Box p={2} bgcolor={`${competitiveAnalysis.positionColor}.50`} borderRadius={2}>
          <Typography variant="subtitle2" color={`${competitiveAnalysis.positionColor}.main`} gutterBottom>
            🎯 Estrategia Recomendada
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {competitiveAnalysis.competitivePosition === "Líder" 
              ? "Mantén tu ventaja competitiva y expande a nuevos mercados."
              : competitiveAnalysis.competitivePosition === "Competitivo"
              ? "Enfócate en las oportunidades identificadas para superar a la competencia."
              : "Prioriza mejorar las métricas más débiles para alcanzar el promedio de la industria."
            }
          </Typography>
        </Box>
    </PremiumCard>
  );
}
